import { billetStatus } from "@/shared/selectors/BilletStatusSelector";
import { eventTypes } from "@/shared/selectors/EventTypeSelector";
import {
	dxtToLocalServerTime,
	getMomentTz,
	MesfModal,
} from "@dexteel/mesf-core";
import { ArrowForward } from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";
import { MoveEventData, ProcessedRow } from "../../models/utils";

const moment = getMomentTz();

const badgeStyles = ({ backgroundColor }: { backgroundColor?: string }) => ({
	backgroundColor: backgroundColor || "action.hover",
	borderRadius: "16px",
	px: backgroundColor ? 1.5 : 2,
	py: 0.5,
	textAlign: "center",
	display: "inline-flex",
	alignItems: "center",
	justifyContent: "center",
});

const getDuration = ({
	newStart,
	newEnd,
}: {
	newStart: Date;
	newEnd: Date;
}) => {
	const duration = newEnd.getTime() - newStart.getTime();
	return moment
		.duration(duration, "milliseconds")
		.format("d [days], h [hours], m [minutes]", { trim: "both" });
};

const DateRangeDisplay = ({
	start,
	end,
	label,
	isOld,
}: {
	start: Date;
	end: Date;
	label: string;
	isOld?: boolean;
}) => (
	<Box>
		<Typography
			variant="caption"
			color="textSecondary"
			fontWeight={600}
			sx={{ mb: 1, display: "block" }}
		>
			{label}
		</Typography>
		<Box
			sx={{
				display: "flex",
				alignItems: "flex-end",
				gap: 1.5,
				opacity: isOld ? 0.5 : 1,
			}}
		>
			<Box
				sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
			>
				<Typography
					variant="caption"
					color="textSecondary"
					sx={{ fontSize: "0.65rem" }}
				>
					Start
				</Typography>
				<Box sx={badgeStyles({})}>
					<Typography
						variant="body2"
						fontWeight="bold"
						color={isOld ? "text.disabled" : "text.secondary"}
					>
						{dxtToLocalServerTime(start, "MM/dd/yy HH:mm")}
					</Typography>
				</Box>
			</Box>
			<Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
				-
			</Typography>
			<Box
				sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
			>
				<Typography
					variant="caption"
					color="textSecondary"
					sx={{ fontSize: "0.65rem" }}
				>
					End
				</Typography>
				<Box sx={badgeStyles({})}>
					<Typography
						variant="body2"
						fontWeight="bold"
						color={isOld ? "text.disabled" : "text.secondary"}
					>
						{dxtToLocalServerTime(end, "MM/dd/yy HH:mm")}
					</Typography>
				</Box>
			</Box>
		</Box>
	</Box>
);

const AdjacentEventDisplay = ({
	event,
	position,
}: {
	event: ProcessedRow | null;
	position: "before" | "after";
}) => {
	if (!event) {
		return (
			<Box sx={{ mb: 2 }}>
				<Typography variant="caption" color="textSecondary" fontWeight={600}>
					{position === "before" ? "After" : "Before"} section
				</Typography>
				<Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
					No event
				</Typography>
			</Box>
		);
	}

	const bgColor =
		event.EventTypeCode === "P"
			? billetStatus.find((s) => s.code === event.BilletsStatusCode)
					?.backgroundColor
			: eventTypes.find((e) => e.code === event.EventTypeCode)?.backgroundColor;

	const displayName = event.SectionName || event.EventTypeName || "Unknown";

	return (
		<Box sx={{ mb: 2 }}>
			<Typography variant="caption" color="textSecondary" fontWeight={600}>
				{position === "before" ? "After" : "Before"} section
			</Typography>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
				<Box sx={badgeStyles({ backgroundColor: bgColor })}>
					<Typography variant="body2" fontWeight={500}>
						{displayName}
					</Typography>
				</Box>
				<Typography variant="caption" color="textSecondary">
					({dxtToLocalServerTime(event.OriginalStart!, "MM/dd HH:mm")} -{" "}
					{dxtToLocalServerTime(event.OriginalEnd!, "MM/dd HH:mm")})
				</Typography>
			</Box>
		</Box>
	);
};

interface ConfirmMoveEventModalProps {
	open: boolean;
	loading?: boolean;
	moveData: MoveEventData | null;
	onConfirm: () => void;
	onCancel: () => void;
}

export const ConfirmMoveEventModal = ({
	open,
	loading = false,
	moveData,
	onConfirm,
	onCancel,
}: ConfirmMoveEventModalProps) => {
	if (!moveData) return null;

	const {
		draggedEvent,
		originalStart,
		originalEnd,
		newStart,
		newEnd,
		eventBefore,
		eventAfter,
	} = moveData;

	const sectionBgColor =
		draggedEvent.EventTypeCode === "P"
			? billetStatus.find((s) => s.code === draggedEvent.BilletsStatusCode)
					?.backgroundColor
			: eventTypes.find((e) => e.code === draggedEvent.EventTypeCode)
					?.backgroundColor;

	const displayName =
		draggedEvent.SectionName || draggedEvent.EventTypeName || "Unknown";

	return (
		<MesfModal
			open={open}
			handleClose={onCancel}
			title="Confirm Move Event"
			maxWidth="md"
			fullWidth
		>
			<MesfModal.Content dividers>
				<Box sx={{ p: 1 }}>
					<Box sx={{ mb: 3 }}>
						<Typography
							variant="caption"
							color="textSecondary"
							fontWeight={600}
						>
							Moving Section
						</Typography>
						<Box
							sx={{ display: "flex", alignItems: "center", gap: 2, mt: 0.5 }}
						>
							<Box sx={badgeStyles({ backgroundColor: sectionBgColor })}>
								<Typography variant="body1" fontWeight={600}>
									{displayName}
								</Typography>
							</Box>
							<Typography variant="body2" color="textSecondary">
								Duration: {getDuration({ newStart, newEnd })}
							</Typography>
						</Box>
					</Box>

					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 2,
							mb: 3,
							p: 2,
							backgroundColor: "action.hover",
							borderRadius: 2,
						}}
					>
						<DateRangeDisplay
							start={originalStart}
							end={originalEnd}
							label="From"
							isOld
						/>
						<ArrowForward color="primary" sx={{ mt: 2 }} />
						<DateRangeDisplay start={newStart} end={newEnd} label="To" />
					</Box>

					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: "1fr 1fr",
							gap: 2,
						}}
					>
						<AdjacentEventDisplay event={eventBefore} position="before" />
						<AdjacentEventDisplay event={eventAfter} position="after" />
					</Box>
				</Box>
			</MesfModal.Content>

			<MesfModal.Actions sx={{ px: 3, py: 2 }}>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						width: "100%",
					}}
				>
					<Button variant="outlined" onClick={onCancel} disabled={loading}>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="primary"
						onClick={onConfirm}
						disabled={loading}
					>
						Confirm Move
					</Button>
				</Box>
			</MesfModal.Actions>
		</MesfModal>
	);
};
